import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  Grid,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Key as KeyIcon,
  Palette as BrandingIcon,
  Gavel as LegalIcon,
  Public as CountryIcon,
  Business as IndustryIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon,
  BusinessCenter as CompanySizeIcon,
  Category as EntityTypeIcon,
  Payment as PaymentMethodIcon,
  Label as TagIcon,
  Description as DescriptionIcon,
  Groups as DefaultRolesIcon,
} from '@mui/icons-material';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useConfig } from '../contexts/ConfigContext';
import { useAuth } from '../contexts/AuthContext';
import NumericField from './NumericField';
import { DEFAULT_TRANSFER_FEE_USD, getTransferFeeUsd } from '../utils/bankService';
import PartnerRolesEditor from './common/PartnerRolesEditor';
import { buildDefaultPartnerRoles } from '../utils/partnerRoles';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`config-tabpanel-${index}`}
      aria-labelledby={`config-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, maxHeight: '70vh', overflow: 'auto', pb: 10 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AppConfig = () => {
  const { isSuperAdmin, userProfile } = useAuth();
  const { config, loading, updateConfig } = useConfig();
  const [pdfTextsDraft, setPdfTextsDraft] = useState(() => config.pdfTexts || {});
  const [tabValue, setTabValue] = useState(() => {
    // Persist tab value in sessionStorage and clamp to available range (0-5)
    const savedTab = sessionStorage.getItem('appConfigTab');
    const parsed = savedTab ? parseInt(savedTab, 10) : 0;
    const maxIndex = 8; // Branding, Legal, Countries, Industries, Company Sizes, Tags, Finance, PDF Texts, Default Roles
    return Number.isFinite(parsed) && parsed >= 0 && parsed <= maxIndex ? parsed : 0;
  });
  const [showApiKeys, setShowApiKeys] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentDialog, setCurrentDialog] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [brandingFormData, setBrandingFormData] = useState({});
  const [brandingTimeout, setBrandingTimeout] = useState(null);
  const [financeFormData, setFinanceFormData] = useState(() => config.finance || { creditCardProcessingPercent: 3.1, checkProcessingPercent: 1.5 });

  // Initialize branding form data when config loads
  useEffect(() => {
    if (config.branding) {
      setBrandingFormData(config.branding);
    }
  }, [config.branding]);

  // Initialize finance form data when config loads
  useEffect(() => {
    if (config.finance) {
      setFinanceFormData(config.finance);
    }
  }, [config.finance]);

  // Default Roles: entities for the role pickers + editable draft of config.defaultPartnerRoles
  const [entities, setEntities] = useState([]);
  const [entitiesLoaded, setEntitiesLoaded] = useState(false);
  const [defaultRolesDraft, setDefaultRolesDraft] = useState(() => buildDefaultPartnerRoles(config.defaultPartnerRoles));

  useEffect(() => {
    const q = query(collection(db, 'entities'), orderBy('entityName', 'asc'));
    const unsubscribe = onSnapshot(q, (snap) => {
      setEntities(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setEntitiesLoaded(true);
    }, (error) => console.error('Error fetching entities:', error));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setDefaultRolesDraft(buildDefaultPartnerRoles(config.defaultPartnerRoles, entities));
    // Re-sync only when the saved defaults change or entities first arrive, so unsaved edits survive entity updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.defaultPartnerRoles, entitiesLoaded]);

  // Sync PDF texts draft with config
  useEffect(() => {
    setPdfTextsDraft(config.pdfTexts || {});
  }, [config.pdfTexts]);

  // Debounced update function for branding
  const updateBrandingDebounced = (updatedBranding) => {
    // Clear existing timeout
    if (brandingTimeout) {
      clearTimeout(brandingTimeout);
    }
    
    // Set new timeout for 500ms delay
    const newTimeout = setTimeout(() => {
      updateConfig({ branding: updatedBranding });
    }, 500);
    
    setBrandingTimeout(newTimeout);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (brandingTimeout) {
        clearTimeout(brandingTimeout);
      }
    };
  }, [brandingTimeout]);

  // Cleanup sessionStorage on unmount
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('appConfigTab');
    };
  }, []);

  // Don't render if not Super Admin
  if (!isSuperAdmin()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access Denied: Only Super Administrators can access this page.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading configuration...</Typography>
      </Box>
    );
  }

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Persist tab value to maintain state across re-renders
    sessionStorage.setItem('appConfigTab', newValue.toString());
  };

  

  const toggleApiKeyVisibility = (keyId) => {
    setShowApiKeys(prev => ({
      ...prev,
      [keyId]: !prev[keyId]
    }));
  };

  const openDialog = (dialogType, item = null) => {
    setCurrentDialog(dialogType);
    setEditingItem(item);
    
    if (item) {
      setFormData({ ...item });
    } else {
      // Set default form data based on dialog type
      switch (dialogType) {
        case 'apiKey':
          setFormData({ name: '', value: '', environment: 'Test', status: 'Inactive', description: '' });
          break;
        case 'legal':
          setFormData({ type: '', content: '', lastUpdated: new Date().toISOString() });
          break;
        case 'country':
          setFormData({ name: '', code: '', currency: 'USD', timezone: '', status: 'Active', transportPercent: 0 });
          break;
        case 'industry':
          setFormData({ name: '', description: '', status: 'Active' });
          break;
        case 'companySize':
          setFormData({ name: '', description: '', range: '' });
          break;
        case 'entityType':
          setFormData({ name: '', description: '' });
          break;
        case 'paymentMethod':
          setFormData({ name: '', description: '' });
          break;
        case 'entityTag':
          setFormData({ name: '' });
          break;
        default:
          setFormData({});
      }
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setCurrentDialog('');
    setEditingItem(null);
    setFormData({});
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let updatedConfig = { ...config };
      
      switch (currentDialog) {
        case 'apiKey':
          if (editingItem) {
            // Update existing API key
            const index = updatedConfig.apiKeys.findIndex(key => key.id === editingItem.id);
            if (index !== -1) {
              updatedConfig.apiKeys[index] = { ...formData };
            }
          } else {
            // Add new API key
            const newId = formData.name.toLowerCase().replace(/\s+/g, '_');
            updatedConfig.apiKeys = [...updatedConfig.apiKeys, { ...formData, id: newId }];
          }
          break;
          
        case 'legal':
          if (editingItem) {
            const index = updatedConfig.legal.findIndex(item => item.id === editingItem.id);
            if (index !== -1) {
              updatedConfig.legal[index] = { ...formData, lastUpdated: new Date().toISOString() };
            }
          } else {
            const newId = formData.type.toLowerCase().replace(/\s+/g, '_');
            updatedConfig.legal = [...updatedConfig.legal, { ...formData, id: newId, lastUpdated: new Date().toISOString() }];
          }
          break;
          
        case 'country':
          if (editingItem) {
            const index = updatedConfig.countries.findIndex(item => item.id === editingItem.id);
            if (index !== -1) {
              updatedConfig.countries[index] = { ...formData };
            }
          } else {
            const newId = formData.code.toLowerCase();
            updatedConfig.countries = [...updatedConfig.countries, { ...formData, id: newId }];
          }
          break;
          
        case 'industry':
          if (editingItem) {
            const index = updatedConfig.industries.findIndex(item => item.id === editingItem.id);
            if (index !== -1) {
              updatedConfig.industries[index] = { ...formData };
            }
          } else {
            const newId = formData.name.toLowerCase().replace(/\s+/g, '_');
            updatedConfig.industries = [...updatedConfig.industries, { ...formData, id: newId }];
          }
          break;

        case 'companySize':
          if (editingItem) {
            const index = updatedConfig.companySizes.findIndex(item => item.id === editingItem.id);
            if (index !== -1) {
              updatedConfig.companySizes[index] = { ...formData };
            }
          } else {
            const newId = formData.name.toLowerCase().replace(/\s+/g, '_');
            updatedConfig.companySizes = [...(updatedConfig.companySizes || []), { ...formData, id: newId }];
          }
          break;



        case 'entityType':
          if (editingItem) {
            const index = updatedConfig.entityTypes.findIndex(item => item.id === editingItem.id);
            if (index !== -1) {
              updatedConfig.entityTypes[index] = { ...formData };
            }
          } else {
            const newId = formData.name.toLowerCase().replace(/\s+/g, '_');
            updatedConfig.entityTypes = [...(updatedConfig.entityTypes || []), { ...formData, id: newId }];
          }
          break;

        case 'paymentMethod':
          if (editingItem) {
            const index = updatedConfig.paymentMethods.findIndex(item => item.id === editingItem.id);
            if (index !== -1) {
              updatedConfig.paymentMethods[index] = { ...formData };
            }
          } else {
            const newId = formData.name.toLowerCase().replace(/\s+/g, '_');
            updatedConfig.paymentMethods = [...(updatedConfig.paymentMethods || []), { ...formData, id: newId }];
          }
          break;

        case 'entityTag':
          if (editingItem) {
            const index = (updatedConfig.entityTags || []).findIndex(item => item.id === editingItem.id);
            if (index !== -1) {
              updatedConfig.entityTags[index] = { ...formData };
            }
          } else {
            const newId = formData.name.toLowerCase().replace(/\s+/g, '_');
            updatedConfig.entityTags = [...(updatedConfig.entityTags || []), { ...formData, id: newId }];
          }
          break;
          
        case 'branding':
          updatedConfig.branding = { ...formData };
          break;
      }
      
      const success = await updateConfig(updatedConfig);
      if (success) {
        showSnackbar(editingItem ? 'Item updated successfully' : 'Item added successfully');
        closeDialog();
      } else {
        showSnackbar('Failed to save changes', 'error');
      }
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar('Error saving changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, itemId) => {
    // These types cannot be deleted, only disabled
    const restrictedTypes = ['country', 'industry', 'companySize', 'entityType', 'paymentMethod'];
    
    if (restrictedTypes.includes(type)) {
      const messages = {
        country: 'Countries cannot be deleted. You can only disable them by editing their status.',
        industry: 'Industries cannot be deleted. You can only disable them by editing their status.',
        companySize: 'Company sizes cannot be deleted. You can only disable them by editing.',
        entityType: 'Entity types are fixed and cannot be deleted.',
        paymentMethod: 'Payment methods are fixed and cannot be deleted.'
      };
      showSnackbar(messages[type], 'warning');
      return;
    }

    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        let updatedConfig = { ...config };
        
        switch (type) {
          case 'apiKey':
            updatedConfig.apiKeys = updatedConfig.apiKeys.filter(key => key.id !== itemId);
            break;
          case 'legal':
            updatedConfig.legal = updatedConfig.legal.filter(item => item.id !== itemId);
            break;
          case 'entityTag':
            updatedConfig.entityTags = (updatedConfig.entityTags || []).filter(item => item.id !== itemId);
            break;
          // Note: industry, companySize, entityType, paymentMethod 
          // are now protected and handled above
        }
        
        const success = await updateConfig(updatedConfig);
        if (success) {
          showSnackbar('Item deleted successfully');
        } else {
          showSnackbar('Failed to delete item', 'error');
        }
      } catch (error) {
        console.error('Error deleting:', error);
        showSnackbar('Error deleting item', 'error');
      }
    }
  };

  const renderApiKeys = () => (
    <Card>
      <CardHeader
        title="API Keys Management"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDialog('apiKey')}
          >
            Add API Key
          </Button>
        }
      />
      <CardContent>
        <List>
          {config.apiKeys?.map((apiKey) => (
            <ListItem key={apiKey.id} divider>
              <ListItemText
                primary={apiKey.name}
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {apiKey.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                      <Chip
                        label={apiKey.environment}
                        size="small"
                        color={apiKey.environment === 'Production' ? 'error' : 'info'}
                      />
                      <Chip
                        label={apiKey.status}
                        size="small"
                        color={apiKey.status === 'Active' ? 'success' : 'default'}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}
                    >
                      {showApiKeys[apiKey.id] ? apiKey.value : '••••••••••••••••'}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    onClick={() => toggleApiKeyVisibility(apiKey.id)}
                    size="small"
                  >
                    {showApiKeys[apiKey.id] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                  <IconButton
                    onClick={() => openDialog('apiKey', apiKey)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete('apiKey', apiKey.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );

  const renderBranding = () => (
    <Card>
      <CardHeader title="Branding Elements" />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Company Name"
              fullWidth
              value={brandingFormData?.companyName || ''}
              onChange={(e) => {
                const updatedBranding = { ...brandingFormData, companyName: e.target.value };
                setBrandingFormData(updatedBranding);
                updateBrandingDebounced(updatedBranding);
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Tagline"
              fullWidth
              value={brandingFormData?.tagline || ''}
              onChange={(e) => {
                const updatedBranding = { ...brandingFormData, tagline: e.target.value };
                setBrandingFormData(updatedBranding);
                updateBrandingDebounced(updatedBranding);
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Primary Color"
              type="color"
              fullWidth
              value={brandingFormData?.primaryColor || '#1976d2'}
              onChange={(e) => {
                const updatedBranding = { ...brandingFormData, primaryColor: e.target.value };
                setBrandingFormData(updatedBranding);
                updateBrandingDebounced(updatedBranding);
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Secondary Color"
              type="color"
              fullWidth
              value={brandingFormData?.secondaryColor || '#dc004e'}
              onChange={(e) => {
                const updatedBranding = { ...brandingFormData, secondaryColor: e.target.value };
                setBrandingFormData(updatedBranding);
                updateBrandingDebounced(updatedBranding);
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderLegalTexts = () => (
    <Card>
      <CardHeader
        title="Legal Documents"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDialog('legal')}
          >
            Add Document
          </Button>
        }
      />
      <CardContent>
        <List>
          {config.legal?.map((legal) => (
            <ListItem key={legal.id} divider>
              <ListItemText
                primary={legal.type}
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {legal.content.substring(0, 100)}...
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last updated: {new Date(legal.lastUpdated).toLocaleDateString()}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    onClick={() => openDialog('legal', legal)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete('legal', legal.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );

  const renderCountries = () => (
    <Card>
      <CardHeader
        title="Countries & Regions"
        subheader="Once a country exists, it cannot be deleted. You can only disable it."
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDialog('country')}
          >
            Add Country
          </Button>
        }
      />
      <CardContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Note:</strong> Countries cannot be deleted once created. You can only change their status to Active or Inactive.
        </Alert>
        <List>
          {config.countries?.map((country) => (
            <ListItem key={country.id} divider>
              <ListItemText
                primary={country.name}
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Chip label={country.code} size="small" variant="outlined" />
                    <Chip label={country.currency} size="small" variant="outlined" />
                    <Chip label={country.timezone} size="small" variant="outlined" />
                    <Chip label={`Transport: ${country.transportPercent || 0}%`} size="small" variant="outlined" />
                    <Chip
                      label={country.status}
                      size="small"
                      color={country.status === 'Active' ? 'success' : 'default'}
                    />
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    onClick={() => openDialog('country', country)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <Tooltip title="Countries cannot be deleted, only disabled">
                    <IconButton
                      disabled
                      size="small"
                      sx={{ color: 'text.disabled' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );

  const renderPdfTexts = () => (
    <Card>
      <CardHeader
        title="PDF Texts"
        subheader="Edit the default text blocks used for Proposal and Contract PDFs"
        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={async () => {
              const success = await updateConfig({ pdfTexts: pdfTextsDraft });
              setSnackbar({ open: true, message: success ? 'PDF texts saved' : 'Failed to save', severity: success ? 'success' : 'error' });
            }}
          >
            Save PDF Texts
          </Button>
        }
      />
      <CardContent>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Proposals</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              label="Intro Paragraph"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.proposals?.intro || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                proposals: { ...(pdfTextsDraft?.proposals || {}), intro: e.target.value }
              })}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Discount Note"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.proposals?.discountNote || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                proposals: { ...(pdfTextsDraft?.proposals || {}), discountNote: e.target.value }
              })}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Contracts</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Document Title"
              fullWidth
              value={pdfTextsDraft?.contracts?.documentTitle || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: { ...(pdfTextsDraft?.contracts || {}), documentTitle: e.target.value }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Between Heading"
              fullWidth
              value={pdfTextsDraft?.contracts?.betweenHeading || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: { ...(pdfTextsDraft?.contracts || {}), betweenHeading: e.target.value }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="And Heading"
              fullWidth
              value={pdfTextsDraft?.contracts?.andHeading || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: { ...(pdfTextsDraft?.contracts || {}), andHeading: e.target.value }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="UCENA Block (one per line)"
              fullWidth
              multiline
              minRows={4}
              value={(pdfTextsDraft?.contracts?.ucenaBlock || []).join('\n')}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: { ...(pdfTextsDraft?.contracts || {}), ucenaBlock: e.target.value.split('\n') }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Fees & Payments Intro"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.feesAndPaymentsIntro || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), feesAndPaymentsIntro: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Delayed Payment Clause"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.delayedPaymentClause || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), delayedPaymentClause: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="2. Services & Scope"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.servicesAndScope || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), servicesAndScope: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="3. Client Obligations"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.clientObligations || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), clientObligations: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="4. Third-Party Expenses"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.expensesThirdParty || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), expensesThirdParty: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="5. Cancellation"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.cancellation || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), cancellation: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="6. Intellectual Property"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.intellectualProperty || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), intellectualProperty: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="7. Data Custody"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.noDataCustody || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), noDataCustody: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="8. Non-Solicitation"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.nonSolicitation || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), nonSolicitation: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="9. Independent Contractors"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.independentContractors || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), independentContractors: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="10. Force Majeure"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.forceMajeure || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), forceMajeure: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="11. Liability Limitation"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.liabilityLimitation || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), liabilityLimitation: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="12. Assignment"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.assignment || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), assignment: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="13. Dispute Resolution"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.disputeResolution || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), disputeResolution: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="14. Notices"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.notices || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), notices: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="15. Entire Agreement"
              fullWidth
              multiline
              minRows={3}
              value={pdfTextsDraft?.contracts?.sections?.entireAgreement || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: {
                  ...(pdfTextsDraft?.contracts || {}),
                  sections: { ...(pdfTextsDraft?.contracts?.sections || {}), entireAgreement: e.target.value }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="UCENA Signatory Name"
              fullWidth
              value={pdfTextsDraft?.contracts?.signatoryName || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: { ...(pdfTextsDraft?.contracts || {}), signatoryName: e.target.value }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="UCENA Signatory Title"
              fullWidth
              value={pdfTextsDraft?.contracts?.signatoryTitle || ''}
              onChange={(e) => setPdfTextsDraft({
                ...pdfTextsDraft,
                contracts: { ...(pdfTextsDraft?.contracts || {}), signatoryTitle: e.target.value }
              })}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderFinance = () => (
    <Card>
      <CardHeader
        title="Finance"
        subheader="Configure payment processing fees applied to Scheduled payments and deposits"
        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={async () => {
              const cc = Math.max(0, Math.min(100, Number(financeFormData?.creditCardProcessingPercent || 0)));
              const chk = Math.max(0, Math.min(100, Number(financeFormData?.checkProcessingPercent || 0)));
              const transferFeeUsd = getTransferFeeUsd(financeFormData);
              const success = await updateConfig({ finance: { creditCardProcessingPercent: cc, checkProcessingPercent: chk, transferFeeUsd } });
              setSnackbar({ open: true, message: success ? 'Finance settings saved' : 'Failed to save', severity: success ? 'success' : 'error' });
            }}
          >
            Save Finance
          </Button>
        }
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <NumericField
              label="Credit Card Processing Fee (%)"
              fullWidth
              value={financeFormData?.creditCardProcessingPercent ?? null}
              onValueChange={(n) => setFinanceFormData(prev => ({ ...prev, creditCardProcessingPercent: n == null ? '' : n }))}
              mode="decimal"
              decimals={1}
              suffix="%"
              min={0}
              max={100}
              helperText="Enter a value between 0 and 100 (e.g., 2.8)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <NumericField
              label="Check Processing Fee (%)"
              fullWidth
              value={financeFormData?.checkProcessingPercent ?? null}
              onValueChange={(n) => setFinanceFormData(prev => ({ ...prev, checkProcessingPercent: n == null ? '' : n }))}
              mode="decimal"
              decimals={1}
              suffix="%"
              min={0}
              max={100}
              helperText="Enter a value between 0 and 100 (e.g., 1.5)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <NumericField
              label="Transfer Fee ($)"
              fullWidth
              value={financeFormData?.transferFeeUsd ?? DEFAULT_TRANSFER_FEE_USD}
              onValueChange={(n) => setFinanceFormData(prev => ({ ...prev, transferFeeUsd: n == null ? '' : n }))}
              mode="decimal"
              decimals={2}
              prefix="$"
              min={0}
              helperText="Flat fee charged per bank-transfer (direct) deposit"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderDefaultRoles = () => (
    <Card>
      <CardHeader
        title="Default Roles"
        subheader="Roles, assignees and percentages pre-filled when creating a new proposal"
        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={async () => {
              const defaultPartnerRoles = defaultRolesDraft.map((r) => ({
                positionId: r.positionId,
                positionName: r.positionName || '',
                entityId: r.entityId || null,
                entityName: r.entityName || '',
                percent: Number(r.percent) || 0,
              }));
              const success = await updateConfig({ defaultPartnerRoles });
              setSnackbar({ open: true, message: success ? 'Default roles saved' : 'Failed to save', severity: success ? 'success' : 'error' });
            }}
          >
            Save Default Roles
          </Button>
        }
      />
      <CardContent>
        <PartnerRolesEditor
          roles={defaultRolesDraft}
          entities={entities}
          onChange={setDefaultRolesDraft}
          onNotify={({ message, severity }) => setSnackbar({ open: true, message, severity })}
        />
      </CardContent>
    </Card>
  );

  const renderIndustries = () => (
    <Card>
      <CardHeader
        title="Industry Categories"
        subheader="Industries cannot be deleted. You can only disable them."
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDialog('industry')}
          >
            Add Industry
          </Button>
        }
      />
      <CardContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Note:</strong> Industries cannot be deleted once created. You can only change their status to Active or Inactive.
        </Alert>
        <List>
          {config.industries?.map((industry) => (
            <ListItem key={industry.id} divider>
              <ListItemText
                primary={industry.name}
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {industry.description}
                    </Typography>
                    <Chip
                      label={industry.status}
                      size="small"
                      color={industry.status === 'Active' ? 'success' : 'default'}
                    />
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    onClick={() => openDialog('industry', industry)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <Tooltip title="Industries cannot be deleted, only disabled">
                    <IconButton
                      disabled
                      size="small"
                      sx={{ color: 'text.disabled' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );

  const renderCompanySizes = () => (
    <Card>
      <CardHeader
        title="Company Size Categories"
        subheader="Company sizes cannot be deleted. You can only disable them."
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDialog('companySize')}
          >
            Add Size
          </Button>
        }
      />
      <CardContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Note:</strong> Company sizes cannot be deleted once created. You can only modify them.
        </Alert>
        <List>
          {config.companySizes?.map((size) => (
            <ListItem key={size.id} divider>
              <ListItemText
                primary={size.name}
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {size.description}
                    </Typography>
                    <Chip label={size.range} size="small" variant="outlined" />
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    onClick={() => openDialog('companySize', size)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <Tooltip title="Company sizes cannot be deleted">
                    <IconButton
                      disabled
                      size="small"
                      sx={{ color: 'text.disabled' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );



  const renderEntityTypes = () => (
    <Card>
      <CardHeader
        title="Entity Types"
        subheader="Fixed entity types: Company, Vendor, Partner, Employee"
        action={
          <Tooltip title="Entity types are fixed and cannot be added">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled
              sx={{ color: 'text.disabled' }}
            >
              Add Entity Type
            </Button>
          </Tooltip>
        }
      />
      <CardContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Fixed Entity Types:</strong> Only Company, Vendor, Partner, and Employee are allowed. No additions or deletions permitted.
        </Alert>
        <List>
          {config.entityTypes?.map((entityType) => (
            <ListItem key={entityType.id} divider>
              <ListItemText
                primary={entityType.name}
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    {entityType.description}
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    onClick={() => openDialog('entityType', entityType)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <Tooltip title="Entity types are fixed and cannot be deleted">
                    <IconButton
                      disabled
                      size="small"
                      sx={{ color: 'text.disabled' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );

  const renderPaymentMethods = () => (
    <Card>
      <CardHeader
        title="Payment Methods"
        subheader="Fixed payment methods: Direct Account, Sub Account"
        action={
          <Tooltip title="Payment methods are fixed and cannot be added">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled
              sx={{ color: 'text.disabled' }}
            >
              Add Payment Method
            </Button>
          </Tooltip>
        }
      />
      <CardContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Fixed Payment Methods:</strong> Only Direct Account and Sub Account are allowed. No additions or deletions permitted.
        </Alert>
        <List>
          {config.paymentMethods?.map((method) => (
            <ListItem key={method.id} divider>
              <ListItemText
                primary={method.name}
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    {method.description}
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    onClick={() => openDialog('paymentMethod', method)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <Tooltip title="Payment methods are fixed and cannot be deleted">
                    <IconButton
                      disabled
                      size="small"
                      sx={{ color: 'text.disabled' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );

  const renderEntityTags = () => (
    <Card>
      <CardHeader
        title="Tags"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDialog('entityTag')}
          >
            Add Tag
          </Button>
        }
      />
      <CardContent>
        <List>
          {config.entityTags?.map((tag) => (
            <ListItem key={tag.id} divider>
              <ListItemText primary={tag.name} />
              <ListItemSecondaryAction>
                <Stack direction="row" spacing={1}>
                  <IconButton onClick={() => openDialog('entityTag', tag)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete('entityTag', tag.id)} color="error" size="small">
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );

  const renderDialog = () => {
    const getDialogTitle = () => {
      const action = editingItem ? 'Edit' : 'Add';
      switch (currentDialog) {
        case 'apiKey': return `${action} API Key`;
        case 'legal': return `${action} Legal Document`;
        case 'country': return `${action} Country`;
        case 'industry': return `${action} Industry`;
        case 'companySize': return `${action} Company Size`;
        case 'entityType': return `${action} Entity Type`;
        case 'paymentMethod': return `${action} Payment Method`;
        case 'entityTag': return `${action} Entity Tag`;
        default: return 'Edit Item';
      }
    };

    const renderDialogContent = () => {
      switch (currentDialog) {
        case 'apiKey':
          return (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  fullWidth
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="API Key Value"
                  fullWidth
                  type="password"
                  value={formData.value || ''}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Environment</InputLabel>
                  <Select
                    value={formData.environment || 'Test'}
                    label="Environment"
                    onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                  >
                    <MenuItem value="Test">Test</MenuItem>
                    <MenuItem value="Production">Production</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status || 'Inactive'}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          );
          
        case 'legal':
          return (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Document Type"
                  fullWidth
                  value={formData.type || ''}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Content"
                  fullWidth
                  multiline
                  rows={6}
                  value={formData.content || ''}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                />
              </Grid>
            </Grid>
          );
          
        case 'country':
          return (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Country Name"
                  fullWidth
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Country Code"
                  fullWidth
                  value={formData.code || ''}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Currency"
                  fullWidth
                  value={formData.currency || ''}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Timezone"
                  fullWidth
                  value={formData.timezone || ''}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <NumericField
                  label="Transport %"
                  fullWidth
                  value={formData.transportPercent ?? null}
                  onValueChange={(n) => setFormData({ ...formData, transportPercent: n ?? 0 })}
                  mode="decimal"
                  decimals={1}
                  suffix="%"
                  min={0}
                  max={35}
                  helperText="Transport percentage (0-35%)"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status || 'Active'}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          );
          
        case 'industry':
          return (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Industry Name"
                  fullWidth
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status || 'Active'}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          );

        case 'companySize':
          return (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Size Name"
                  fullWidth
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Employee Range"
                  fullWidth
                  placeholder="e.g., 1-50, 51-200, 1000+"
                  value={formData.range || ''}
                  onChange={(e) => setFormData({ ...formData, range: e.target.value })}
                />
              </Grid>
            </Grid>
          );



        case 'entityType':
          return (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Entity Type Name"
                  fullWidth
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
            </Grid>
          );

        case 'paymentMethod':
          return (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Payment Method Name"
                  fullWidth
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
            </Grid>
          );
        case 'entityTag':
          return (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Tag Name"
                  fullWidth
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
            </Grid>
          );
          
        default:
          return null;
      }
    };

    return (
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {renderDialogContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          🛠️ App Configuration
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Centralized application configurations and system settings
        </Typography>
      </Box>

    

      {/* Configuration Tabs */}
      <Paper>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {/* <Tab icon={<KeyIcon />} label="API Keys" /> */}
          <Tab icon={<BrandingIcon />} label="Branding" />
          <Tab icon={<LegalIcon />} label="Legal Texts" />
          <Tab icon={<CountryIcon />} label="Countries" />
          <Tab icon={<IndustryIcon />} label="Industries" />
          <Tab icon={<CompanySizeIcon />} label="Company Sizes" />
          <Tab icon={<TagIcon />} label="Tags" />
          <Tab icon={<PaymentMethodIcon />} label="Finance" />
          <Tab icon={<DescriptionIcon />} label="PDF Texts" />
          <Tab icon={<DefaultRolesIcon />} label="Default Roles" />
          {/* <Tab icon={<EntityTypeIcon />} label="Entity Types" /> */}
          {/* <Tab icon={<PaymentMethodIcon />} label="Payment Methods" /> */}
        </Tabs>
{/* 
        <TabPanel value={tabValue} index={0}>
          {renderApiKeys()}
        </TabPanel> */}

        <TabPanel value={tabValue} index={0}>
          {renderBranding()}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {renderLegalTexts()}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {renderCountries()}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {renderIndustries()}
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          {renderCompanySizes()}
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          {renderEntityTags()}
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          {renderFinance()}
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          {renderPdfTexts()}
        </TabPanel>

        <TabPanel value={tabValue} index={8}>
          {renderDefaultRoles()}
        </TabPanel>

        {/* <TabPanel value={tabValue} index={9}>
          {renderEntityTypes()}
        </TabPanel>

        <TabPanel value={tabValue} index={10}>
          {renderPaymentMethods()}
        </TabPanel> */}
      </Paper>

      {/* Dialog */}
      {renderDialog()}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppConfig; 